import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ArrowLeftOutlined, CloseCircleOutlined, DeleteOutlined, EditOutlined, MessageOutlined, ToolOutlined, } from "@ant-design/icons";
import { Button, Card, Flex, Modal, Skeleton, Tooltip, Typography } from "antd";
import { message } from "antd";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
// Components
import ClientMirrorPanel from "@/components/OrderDetail/ClientMirrorPanel";
import DispatcherServiceSidebar from "@/components/OrderDetail/DispatcherServiceSidebar";
import OrderCancelModal from "@/components/OrderDetail/OrderCancelModal";
import OrderForm from "@/components/OrderForm/OrderForm";
import { OrderStatusTag } from "@/components/OrderForm/OrderStatusTag";
import OrderStatusTracker from "@/components/OrderForm/OrderStatusTracker";
// Stores
import { getRootStore } from "@/stores/RootStore";
// Types, utils, styles
import styles from "@/styles/OrderPage.module.less";
import { onError } from "@/utils/onError";
import { canCancelOrder } from "@/utils/orderCancel";
import { getActiveRouteInfo } from "@/utils/orderRoute";
import { canAccessChat } from "@/utils/permissions";
const { Text } = Typography;
const OrderPage = observer(() => {
    const navigate = useNavigate();
    const { orderId } = useParams();
    const { singleOrderStore: store, chatStore, authStore, orderDetailStore } = getRootStore();
    const { isLoading, clientSelectStore, tariffSelectStore, } = store;
    const [isStatusTrackerVisible, setIsStatusTrackerVisible] = useState(false);
    const [isCancelOpen, setIsCancelOpen] = useState(false);
    // canEditOrder / canDelete / availableStatuses are MobX-computed — read inside observer
    const canEditOrder = store.canEditOrder();
    const canDeleteOrder = store.canDeleteOrder();
    const showStatusTracker = store.availableStatuses.length > 0;
    // WP-DSP-FIX-03: единственный путь отмены — кнопка «Отменить» с причиной
    const showCancel = store.order !== null && canCancelOrder(store.order.status);
    // isEditOpen is a MobX observable on the store
    const isEditOpen = store.isEditOpen;
    // Load order data and reference data
    useEffect(() => {
        store.init(orderId);
        if (orderId) {
            orderDetailStore.load(orderId);
            orderDetailStore.startRealtime(orderId);
        }
        return () => {
            store.clearOrderData();
            orderDetailStore.stopRealtime();
        };
    }, [orderId]);
    const handleBack = () => {
        navigate("/orders");
    };
    const closeStatusTracker = () => {
        setIsStatusTrackerVisible(false);
    };
    const handleSave = async (values) => {
        const isNewOrder = !store.order;
        store.saveOrder(values).then((savedOrder) => {
            if (savedOrder) {
                if (isNewOrder) {
                    message.success("Заказ успешно создан");
                    navigate(`/orders/${savedOrder.id}`);
                }
                else {
                    message.success("Заказ успешно обновлен");
                    store.closeEdit();
                }
            }
        });
    };
    const handleDeleteOrder = () => {
        const order = store.order;
        if (!order)
            return;
        Modal.confirm({
            title: "Удаление заказа",
            content: "Вы уверены, что хотите удалить этот заказ?",
            okText: "Удалить",
            okType: "danger",
            cancelText: "Отмена",
            onOk: async () => {
                try {
                    // FR-007 (UC-107): отказ (CP013) уже показан стором — без «успешно удалён» и ухода со страницы
                    const deleted = await store.deleteOrder(order.id);
                    if (!deleted)
                        return;
                    message.success("Заказ успешно удален");
                    handleBack();
                }
                catch (error) {
                    onError(error, "Error deleting order");
                }
            },
        });
    };
    if (isLoading) {
        return (_jsx("div", { className: styles.container, children: _jsx(Card, { children: _jsx(Skeleton, { active: true, paragraph: { rows: 8 } }) }) }));
    }
    // ── CREATE mode (no orderId): render full-page OrderForm as before ──────────
    if (!orderId) {
        return (_jsxs(Flex, { vertical: true, gap: "large", className: styles.container, children: [_jsx("div", { className: styles.backButtonContainer, children: _jsx(Button, { icon: _jsx(ArrowLeftOutlined, {}), onClick: handleBack, type: "text", className: styles.backButton, children: "\u041D\u0430\u0437\u0430\u0434 \u043A \u0441\u043F\u0438\u0441\u043A\u0443" }) }), _jsx(OrderForm, { initialFormData: store.initialFormData, onSave: handleSave, onCancel: handleBack, isLoading: store.isSaving, order: store.order || undefined, disabled: false, tableSelectStores: {
                        clientSelectStore,
                        tariffSelectStore,
                    } })] }));
    }
    // ── VIEW mode (orderId present): header + monitoring grid ──────────────────
    const order = store.order;
    const orderNumber = order?.orderNumber ?? orderId.slice(0, 8);
    return (_jsxs(Flex, { vertical: true, gap: "large", className: styles.container, children: [_jsx(Card, { className: styles.headerCard, styles: { body: { padding: "16px 24px" } }, children: _jsxs("div", { className: styles.headerInner, children: [_jsxs("div", { className: styles.headerLeft, children: [_jsx(Button, { icon: _jsx(ArrowLeftOutlined, {}), onClick: handleBack, type: "text", size: "small", className: styles.backButton, children: "\u041D\u0430\u0437\u0430\u0434" }), _jsxs(Text, { strong: true, className: styles.headerTitle, children: ["\u0417\u0430\u043A\u0430\u0437 #", orderNumber] }), order !== null && (_jsx(OrderStatusTag, { status: order.status }))] }), _jsxs("div", { className: styles.headerActions, children: [showStatusTracker && (_jsx(Button, { type: "primary", icon: _jsx(ToolOutlined, {}), onClick: () => setIsStatusTrackerVisible(true), children: "\u0418\u0437\u043C\u0435\u043D\u0438\u0442\u044C \u0441\u0442\u0430\u0442\u0443\u0441" })), canAccessChat(authStore.userRole) && (order && getActiveRouteInfo(order.routeInfo) ? (_jsx(Button, { icon: _jsx(MessageOutlined, {}), onClick: () => chatStore.openPanelWithOrder(order.id), children: "\u0427\u0430\u0442" })) : (_jsx(Tooltip, { title: "\u0417\u0430\u043A\u0430\u0437 \u043D\u0435 \u043D\u0430\u0437\u043D\u0430\u0447\u0435\u043D \u043D\u0430 \u043C\u0430\u0440\u0448\u0440\u0443\u0442", children: _jsx(Button, { icon: _jsx(MessageOutlined, {}), disabled: true, children: "\u0427\u0430\u0442" }) }))), showCancel ? (_jsx(Button, { danger: true, icon: _jsx(CloseCircleOutlined, {}), onClick: () => setIsCancelOpen(true), "data-testid": "order__cancel_button", children: "\u041E\u0442\u043C\u0435\u043D\u0438\u0442\u044C" })) : null, canDeleteOrder && (_jsx(Button, { danger: true, icon: _jsx(DeleteOutlined, {}), onClick: handleDeleteOrder, children: "\u0423\u0434\u0430\u043B\u0438\u0442\u044C" })), canEditOrder && (_jsx(Button, { icon: _jsx(EditOutlined, {}), onClick: () => store.openEdit(), children: "\u0420\u0435\u0434\u0430\u043A\u0442\u0438\u0440\u043E\u0432\u0430\u0442\u044C" }))] })] }) }), _jsxs("div", { className: styles.monitoringGrid, children: [_jsx("div", { className: styles.monitoringMain, children: _jsx(ClientMirrorPanel, {}) }), _jsx("div", { className: styles.monitoringSidebar, children: _jsx(DispatcherServiceSidebar, {}) })] }), isEditOpen && (_jsx(Modal, { open: true, title: `Редактирование заказа #${orderNumber}`, onCancel: () => store.closeEdit(), footer: null, width: 900, destroyOnClose: true, children: _jsx(OrderForm, { initialFormData: store.initialFormData, onSave: handleSave, onCancel: () => store.closeEdit(), isLoading: store.isSaving, order: store.order || undefined, disabled: false, tableSelectStores: {
                        clientSelectStore,
                        tariffSelectStore,
                    } }) })), isCancelOpen ? _jsx(OrderCancelModal, { onClose: () => setIsCancelOpen(false) }) : null, isStatusTrackerVisible && (_jsx(Modal, { open: true, title: "\u0418\u0437\u043C\u0435\u043D\u0435\u043D\u0438\u0435 \u0441\u0442\u0430\u0442\u0443\u0441\u0430 \u0437\u0430\u043A\u0430\u0437\u0430", onCancel: () => setIsStatusTrackerVisible(false), footer: null, width: 800, children: _jsx(OrderStatusTracker, { store: store, onFinish: closeStatusTracker }) }))] }));
});
OrderPage.displayName = "OrderPage";
export default OrderPage;
